import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, ShieldAlert, FileText, Link2 } from 'lucide-react'
import { adminApi } from '@/api/admin'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

const SUGGESTED_NOTICE =
  'We keep your photo so that anyone checking your training certificate can confirm the person named on it is you. ' +
  'It is stored securely, only shown where we have told you, and you can ask us to remove it at any time.'

/** Settings -> Photos: switch identity photos on and set the notice shown wherever one is added. */
export default function PhotoSettingsCard() {
  const queryClient = useQueryClient()
  const [enabled, setEnabled] = useState<boolean | null>(null)
  const [notice, setNotice] = useState('')
  const [showOnVerification, setShowOnVerification] = useState(false)
  const [agree, setAgree] = useState(false)
  const [saved, setSaved] = useState(false)

  const { data } = useQuery({
    queryKey: ['photo-settings'],
    queryFn: () => adminApi.getPhotoSettings().then(r => r.data),
  })

  const { data: company } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })
  const [copied, setCopied] = useState(false)

  const { data: terms } = useQuery({
    queryKey: ['data-processing-status'],
    queryFn: () => adminApi.getDataProcessingStatus().then(r => r.data),
  })

  const acceptMutation = useMutation({
    mutationFn: () => adminApi.acceptDataProcessing(),
    onSuccess: () => {
      setAgree(false)
      queryClient.invalidateQueries({ queryKey: ['data-processing-status'] })
    },
  })

  if (data && enabled === null) {
    setEnabled(data.enabled)
    setNotice(data.notice ?? '')
    setShowOnVerification(data.showOnVerification)
  }

  const mutation = useMutation({
    mutationFn: () => adminApi.updatePhotoSettings({ enabled: !!enabled, notice: notice.trim() || null, showOnVerification: !!enabled && showOnVerification }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['photo-settings'] })
      queryClient.invalidateQueries({ queryKey: ['portal-photo-settings'] })
      queryClient.invalidateQueries({ queryKey: ['attendee-photo-settings'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
  })

  if (!data || !terms || enabled === null) return <p className="text-sm text-gray-400">Loading…</p>

  // Turning photos ON needs the terms accepted (the API enforces this too); an existing setup can keep saving.
  const mayEnable = data.enabled || terms.acceptedVersion !== null

  const dirty = enabled !== data.enabled || notice.trim() !== (data.notice ?? '')
    || (enabled && showOnVerification) !== data.showOnVerification

  return (
    <form onSubmit={e => { e.preventDefault(); mutation.mutate() }} className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader><CardTitle className="text-base">Identity photos</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-gray-500">
            Identity photos help confirm that the person in front of a trainer is the person on the register and the
            certificate. Photos can be added by your administrators, by a client's managers for their own staff, by
            trainers at the register (using the phone camera), and by attendees themselves on their own page. Trainers
            see the photos of the people on their sessions. A photo only appears on the public certificate page if you
            turn that on below.
          </p>

          <div className="rounded-lg border border-gray-200 px-4 py-3 space-y-2">
            <div className="flex items-start gap-2.5">
              <FileText size={16} className="text-gray-400 mt-0.5 shrink-0" />
              <div className="text-xs text-gray-600">
                <p className="text-sm font-medium text-gray-900">Data Processing Terms</p>
                <p>
                  These set out what CertSlate does with your people's data on your behalf.{' '}
                  <a href="/legal/data-processing" target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800">Read the terms</a>
                  {' · '}
                  <a href="/legal/photo-guide" target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800">Read the guide to using photos</a>
                </p>
                {terms.upToDate ? (
                  <p className="text-green-700 mt-1">
                    Accepted by {terms.acceptedBy} on {new Date(terms.acceptedAt!).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.
                  </p>
                ) : terms.acceptedVersion !== null ? (
                  <p className="text-amber-700 mt-1">The terms have been updated since you accepted them. Please review and accept the new version.</p>
                ) : (
                  <p className="text-amber-700 mt-1">Not accepted yet. You need to accept them before you can switch photos on.</p>
                )}
              </div>
            </div>
            {!terms.upToDate && (
              <div className="flex flex-wrap items-center gap-3 pl-6">
                <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                  I have read the Data Processing Terms and accept them for my organisation
                </label>
                <Button type="button" size="sm" variant="outline" disabled={!agree || acceptMutation.isPending} onClick={() => acceptMutation.mutate()}>
                  {acceptMutation.isPending ? 'Saving…' : 'Accept'}
                </Button>
              </div>
            )}
          </div>

          <label className={`flex items-start gap-3 ${mayEnable ? 'cursor-pointer' : 'opacity-50'}`}>
            <input
              type="checkbox"
              checked={enabled}
              disabled={!mayEnable}
              onChange={e => setEnabled(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <div>
              <p className="text-sm font-medium text-gray-900">Enable identity photos</p>
              <p className="text-xs text-gray-500">
                Photos are stored privately and only shown to people signed in to your account, the client's own
                managers, and the person themselves. Turning this off hides every photo; you can still remove them.
              </p>
            </div>
          </label>

          <label className={`flex items-start gap-3 ${enabled ? 'cursor-pointer' : 'opacity-50'}`}>
            <input
              type="checkbox"
              checked={enabled && showOnVerification}
              disabled={!enabled}
              onChange={e => setShowOnVerification(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <div>
              <p className="text-sm font-medium text-gray-900">Show the photo when a certificate is verified</p>
              <p className="text-xs text-gray-500">
                Anyone who scans a certificate's QR code or opens its verification link will see the holder's photo, and
                (if a trainer checked them at the course) that their identity was checked. It is only shown for
                current certificates. Off by default: make sure your privacy notice covers this before turning it on.
              </p>
            </div>
          </label>

          <div>
            <label htmlFor="photo-notice" className="text-sm font-medium text-gray-900">Privacy notice</label>
            <p className="text-xs text-gray-500 mb-1.5">Shown wherever a photo is added. Say why you keep it, who can see it, and how to have it removed.</p>
            <textarea
              id="photo-notice"
              value={notice}
              onChange={e => setNotice(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder={SUGGESTED_NOTICE}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
            {!notice.trim() && (
              <button type="button" onClick={() => setNotice(SUGGESTED_NOTICE)} className="text-xs text-indigo-600 hover:text-indigo-800 mt-1 cursor-pointer">
                Use the suggested wording
              </button>
            )}
          </div>

          {company?.slug && (() => {
            const noticeUrl = `${window.location.origin}/privacy/photos/${company.slug}`
            return (
              <div className="rounded-lg border border-gray-200 px-4 py-3 text-xs text-gray-600 space-y-1.5">
                <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5"><Link2 size={14} className="text-gray-400" /> Your public photo notice</p>
                <p>
                  Your notice as a web page, for the people in the photos. Link to it from onboarding emails, your client terms and booking confirmations.
                  The public booking page already links to it.
                </p>
                <div className="flex items-center gap-2">
                  <a href={noticeUrl} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800 break-all">{noticeUrl}</a>
                  <Button type="button" size="sm" variant="outline" onClick={() => { void navigator.clipboard?.writeText(noticeUrl); setCopied(true); setTimeout(() => setCopied(false), 2000) }}>
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </div>
            )
          })()}

          <div className="flex gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
            <ShieldAlert size={16} className="shrink-0 mt-0.5" />
            <p>
              You are responsible for having a lawful reason to hold photos and for telling people about it (your own privacy
              policy should mention them). If someone asks for their photo to be removed, use the Remove button on their record.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={!dirty || mutation.isPending}>{mutation.isPending ? 'Saving…' : 'Save'}</Button>
            {saved && <span className="flex items-center gap-1 text-sm text-green-600"><Check size={14} /> Saved</span>}
            {mutation.isError && <span className="text-sm text-red-600">Could not save.</span>}
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
